import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Lock, 
  Eye, 
  Share2, 
  Server, 
  UserCheck, 
  Cookie, 
  Link as LinkIcon, 
  Baby, 
  FileText, 
  Mail, 
  Menu, 
  ChevronRight,
  ArrowUp
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll spy to update active section in sidebar
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const top = section.offsetTop;
        const height = section.offsetHeight;
        const id = section.getAttribute('id');

        if (scrollPosition >= top && scrollPosition < top + height) {
          setActiveSection(id);
        }
      });

      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // height of sticky header/padding
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { id: 'introduction', label: 'Introduction', icon: Shield },
    { id: 'collection', label: 'Information We Collect', icon: Eye },
    { id: 'usage', label: 'How We Use Info', icon: FileText },
    { id: 'sharing', label: 'Sharing & Disclosure', icon: Share2 },
    { id: 'security', label: 'Data Security', icon: Lock },
    { id: 'rights', label: 'Your Rights', icon: UserCheck },
    { id: 'cookies', label: 'Cookies', icon: Cookie },
    { id: 'links', label: 'Third-Party Links', icon: LinkIcon },
    { id: 'children', label: 'Children\'s Privacy', icon: Baby },
    // { id: 'contact', label: 'Contact Us', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-600 selection:bg-indigo-100 selection:text-indigo-700">
      
      {/* Header Background */}
      <div className="bg-[#1a1f2c] text-white pb-32 pt-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30" 
             style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/cubes.png")` }}>
        </div>
        
        {/* Gradient Overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2a3890]/30 to-[#2a3890]/100"></div>
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 left-0 -mt-20 -ml-20 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <AnimatedSection animationType="fadeUp">
              <h2 className="text-4xl md:text-5xl font-semibold uppercase tracking-tight mb-6">
                Privacy Policy
              </h2>
              {/* <motion.div 
              initial={{ width: 0 }}
              whileInView={{ width: 200 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-[#2a3890] mx-auto rounded-full mt-2"
              /> */}
            </AnimatedSection>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              We value your trust and are committed to protecting your personal information. Please read this document carefully to understand our practices.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 pb-20 relative z-20">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          
          {/* Sidebar Navigation (Desktop) */}
          <div className="hidden lg:block lg:col-span-3">
            <nav className="sticky top-8 space-y-1 bg-white shadow-lg p-4 border border-slate-100">
              <div className="pb-4 mb-4 border-b border-slate-100">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider pl-3">
                  Contents
                </h3>
              </div>
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                      activeSection === item.id
                        ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <Icon className={`mr-3 h-4 w-4 transition-colors ${
                      activeSection === item.id ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-500'
                    }`} />
                    {item.label}
                    {activeSection === item.id && (
                      <ChevronRight className="ml-auto h-4 w-4 text-indigo-400" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden mb-6 sticky top-4 z-30">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-full bg-white shadow-lg rounded-xl p-4 flex items-center justify-between border border-slate-100 text-slate-700 font-medium"
            >
              <span className="flex items-center">
                <Menu className="mr-2 h-5 w-5 text-indigo-600" />
                Jump to Section
              </span>
              <span className="text-sm bg-slate-100 px-2 py-1 rounded text-slate-500">
                {isMobileMenuOpen ? 'Close' : 'Open'}
              </span>
            </button>
            
            {isMobileMenuOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 p-2 z-40 max-h-[60vh] overflow-y-auto">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Main Content */}
          <main className="lg:col-span-9 space-y-8">
            <div className="bg-white shadow-xl border border-slate-100 overflow-hidden">
              <div className="p-6 md:p-10 lg:p-12 space-y-12">

                {/* Introduction */}
                <section id="introduction" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                      <Shield className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Introduction</h2>
                  </div>
                  <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed">
                    <p>
                      TM-Group International ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website. We understand the importance of your data and are dedicated to maintaining its confidentiality and integrity.
                    </p>
                  </div>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* Information We Collect */}
                <section id="collection" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                      <Eye className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Information We Collect</h2>
                  </div>
                  <p className="mb-4">We may collect information about you in a variety of ways. The information we may collect includes:</p>
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    {[
                      "Personal data (Name, email, phone number, mailing address)",
                      "Contact form details & messages",
                      "IP address, browser type, and device info",
                      "Usage data (Pages visited, time spent, navigation)"
                    ].map((item, i) => (
                      <div key={i} className="flex items-start p-4 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="mt-1 mr-3 h-2 w-2 rounded-full bg-blue-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* How We Use Your Information */}
                <section id="usage" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-green-100 rounded-lg text-green-600">
                      <FileText className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">How We Use Your Information</h2>
                  </div>
                  <p className="mb-4">We use the information we collect to:</p>
                  <ul className="space-y-3">
                    {[
                      "Respond to your inquiries and provide customer service",
                      "Send you information about our services and projects",
                      "Improve our website and user experience",
                      "Comply with legal obligations",
                      "Protect our rights and prevent fraud"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start">
                        <ChevronRight className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* Information Sharing */}
                <section id="sharing" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                      <Share2 className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Information Sharing</h2>
                  </div>
                  <div className="bg-purple-50 p-6 rounded-xl border border-purple-100 mb-6">
                    <p className="text-purple-900 font-medium">
                      We do not sell, trade, or rent your personal information to third parties.
                    </p>
                  </div>
                  <p className="mb-4">We may share your information only in the following circumstances:</p>
                  <ul className="list-disc pl-6 space-y-2 marker:text-purple-400">
                    <li>With your consent</li>
                    <li>To comply with legal obligations or respond to lawful requests</li>
                    <li>To protect our rights, privacy, safety, or property</li>
                    <li>With service providers who assist us in operating our website and conducting our business</li>
                  </ul>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* Data Security */}
                <section id="security" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                      <Lock className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Data Security</h2>
                  </div>
                  <p>
                    We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* Your Rights */}
                <section id="rights" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
                      <UserCheck className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Your Rights</h2>
                  </div>
                  <p className="mb-4">You have the right to:</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      "Access and receive a copy of your personal data",
                      "Request correction of inaccurate personal data",
                      "Request deletion of your personal data",
                      "Object to processing of your personal data",
                      "Request restriction of processing your personal data",
                      "Withdraw consent at any time"
                    ].map((right, i) => (
                      <div key={i} className="flex items-center p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mr-3" />
                        <span className="text-sm">{right}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* Cookies */}
                <section id="cookies" className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                      <Cookie className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Cookies & Tracking</h2>
                  </div>
                  <p>
                    We may use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                  </p>
                </section>

                <div className="h-px bg-slate-100 w-full" />

                {/* Other Sections Group */}
                <div className="grid md:grid-cols-2 gap-8">
                  <section id="links" className="scroll-mt-24">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-1.5 bg-gray-100 rounded text-gray-600">
                        <LinkIcon className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">Third-Party Links</h3>
                    </div>
                    <p className="text-sm">
                      Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                    </p>
                  </section>

                  <section id="children" className="scroll-mt-24">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-1.5 bg-pink-100 rounded text-pink-600">
                        <Baby className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">Children's Privacy</h3>
                    </div>
                    <p className="text-sm">
                      Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
                    </p>
                  </section>
                </div>

                <section id="changes" className="scroll-mt-24 p-6 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Changes to This Privacy Policy</h3>
                  <p className="text-sm">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date at the top of this page.
                  </p>
                </section>

                <div className="h-px bg-slate-100 w-full" />

              </div>
            </div>
          </main>
        </div>
      </div>
      
      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-all duration-300 transform ${
          showScrollTop ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
        aria-label="Scroll to top"
      >
        <ArrowUp className="h-6 w-6" />
      </button>

      {/* Animations Style */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;